import { Box, Flex } from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import React, { useMemo, useRef } from "react";
import {
  useGetMcbimFragsQuery,
  useGetMcbimItemsUniqueClassesQuery,
  useGetMultiplacerDevicetypesQuery,
} from "../../../openapi";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { usePrefetchMcBimItemsPerIfcClass } from "../../hooks/usePrefetchMcBimItemsPerIfcClass";
import { selectNrOfMcbimItems, selectnrOfMcbimSpaces } from "../../McBim/mcbimSelectors";
import { selectUniqueVisibleMcbimFileNames } from "../../McBim/extraMcBimSelectors";
import { selectProjectNoAndFloorName } from "../../McBim/McBimSlice";
import { selectProjectNo } from "../../Room/RoomSlice";
import { RIGHT_CLICK_DEVICE_MENU_ENUM } from "@multiconsult-group/multiplacer-core";
import { dragSelectEndThunk, thunkClickOnAPixPolygon, thunkHandleRightMenu, thunkHandle3dObjectClick } from "../designThunk";
import {
  selectFloorElevation,
  selectModRotation,
  selectNextFloorElevation,
} from "../designSelector";
import {
  selectActiveDeviceToActiveRoomDimensionLines,
  selectFloorExtentKey,
  selectPixActionState,
  selectPixPaths,
  selectTransformPivot,
  selectViewerPixPolygons,
  selectRoomNumberingHeatmapPaths,
  selectRoomNumberingHeatmapCircles,
  selectRoomNumberingRestrictionLinePaths,
  NUMBERING_ORIGIN_POLYGON_ID,
} from "../pixpolygonSelectors";
import {
  addRestrictionLine,
  selectRestrictionLine,
  setNumberingOrigin,
} from "../../RoomNumbering/roomNumberingSlice";
import { thunkMoveDeviceOnDragEnd, thunkStartMove } from "../devices/deviceThunks";
import { selectAutoPipeRoutingConnectionPointCircles } from "../routing/autoPipeRouting/autoPipeRoutingSelectors";
import { IS_DEV } from "../../config";
import { MultiViewer } from "@multiconsult-group/multiplacer-ui";
import type { IPixBimCameraState } from "@multiconsult-group/multiplacer-ui";

const readInitialCameraFromUrl = (): { zoom?: number; centerWorld?: { x: number; y: number } } => {
  const sp = new URLSearchParams(window.location.search);

  const z = sp.get("z") ?? sp.get("zoom");
  const zoom = z != null ? Number(z) : undefined;

  const p = sp.get("p") ?? sp.get("pan");
  if (p) {
    const [xRaw, yRaw] = p.split(",");
    const x = Number(xRaw);
    const y = Number(yRaw);
    if (Number.isFinite(x) && Number.isFinite(y)) {
      return { zoom, centerWorld: { x, y } };
    }
  }

  const x = Number(sp.get("x") ?? sp.get("cx"));
  const y = Number(sp.get("y") ?? sp.get("cy"));
  if (Number.isFinite(x) && Number.isFinite(y)) {
    return { zoom, centerWorld: { x, y } };
  }

  return { zoom };
};

const formatNum = (n: number) => String(Math.round(n * 1000) / 1000);

export const DesignTabViewer: React.FC = () => {
  const dispatch = useAppDispatch();
  const initialCamera = useMemo(() => {
    const camera = readInitialCameraFromUrl();
    if (camera.zoom == null || camera.centerWorld == null) return undefined;
    return {
      zoom: camera.zoom,
      centerWorld: camera.centerWorld,
    };
  }, []);
  const lastUrlCamRef = useRef("");
  const projectNo = useAppSelector(selectProjectNo);
  const uniqueVisibleMcbimFileNames = useAppSelector(selectUniqueVisibleMcbimFileNames);
  const floorElevationMm = useAppSelector(selectFloorElevation);
  const nextFloorElevationMm = useAppSelector(selectNextFloorElevation);
  const clipZMm =
    floorElevationMm == null || nextFloorElevationMm == null
      ? null
      : { bottom: floorElevationMm, top: nextFloorElevationMm };

  useGetMultiplacerDevicetypesQuery({ projectNo });
  useGetMcbimItemsUniqueClassesQuery(useAppSelector(selectProjectNoAndFloorName) ?? skipToken);
  usePrefetchMcBimItemsPerIfcClass();

  const {
    data: fragData,
    error: fragFilesError,
    isFetching: isFetchingFragFiles,
    isLoading: isLoadingFragFiles,
  } = useGetMcbimFragsQuery(projectNo ? { projectNo } : skipToken);

  const pixPolygons = useAppSelector(selectViewerPixPolygons);
  const baseCircles = useAppSelector(selectAutoPipeRoutingConnectionPointCircles);
  const basePaths = useAppSelector(selectPixPaths);
  const dimensionLines = useAppSelector(selectActiveDeviceToActiveRoomDimensionLines);
  const heatmapPaths = useAppSelector(selectRoomNumberingHeatmapPaths);
  const heatmapCircles = useAppSelector(selectRoomNumberingHeatmapCircles);
  const restrictionLinePaths = useAppSelector(selectRoomNumberingRestrictionLinePaths);
  const pixPaths = [...basePaths, ...heatmapPaths, ...restrictionLinePaths];
  const pixCircles = [...baseCircles, ...heatmapCircles];
  const rotation = useAppSelector(selectModRotation);
  const transformPivot = useAppSelector(selectTransformPivot);
  const pixActionState = useAppSelector(selectPixActionState);
  const extentKey = useAppSelector(selectFloorExtentKey);
  const zoomExtentNonce = useAppSelector((state) => state.design.zoomExtentNonce);
  const zoomExtentKey = `${extentKey ?? "no-extent"}:${zoomExtentNonce}`;

  const handleCameraChange = (camera: IPixBimCameraState) => {
    if (camera.zoom == null || !camera.centerWorld) return;

    const z = formatNum(camera.zoom);
    const p = `${formatNum(camera.centerWorld.x)},${formatNum(camera.centerWorld.y)}`;
    const camKey = `z=${z}&p=${p}`;

    if (lastUrlCamRef.current === camKey) return;
    lastUrlCamRef.current = camKey;

    const sp = new URLSearchParams(window.location.search);
    sp.set("z", z);
    sp.set("p", p);
    sp.delete("zoom");
    sp.delete("pan");
    sp.delete("x");
    sp.delete("y");
    sp.delete("cx");
    sp.delete("cy");

    const newUrl = `${window.location.pathname}?${sp.toString()}${window.location.hash ?? ""}`;
    window.history.replaceState(null, "", newUrl);
  };

  return (
    <Box h="100%" w="100%" style={{ display: "flex", flexDirection: "column" }}>
      <StatsBar />
      <Box style={{ flex: 1, minHeight: 0 }}>
        <MultiViewer
          twoD={{
            polygons: pixPolygons,
            circles: pixCircles,
            paths: pixPaths,
            dimensionLines,
            angle: rotation,
            action: pixActionState,
            zoomExtentKey,
            transformPivot,
            useLeftClickToPan: IS_DEV,
            flip2dCoordinate: true,
            initialCamera,
            onCameraChange: handleCameraChange,
            onMoveStart: (id) => {
              if (id !== NUMBERING_ORIGIN_POLYGON_ID) {
                dispatch(thunkStartMove(id));
              }
            },
            onRightMenuClick: (action, polygon) =>
              dispatch(thunkHandleRightMenu(action as RIGHT_CLICK_DEVICE_MENU_ENUM, polygon.id)),
            onClick: (id, coordinates) => {
              if (id?.startsWith("numbering-restriction-line-")) {
                dispatch(selectRestrictionLine(id.replace("numbering-restriction-line-", "")));
              } else if (id === NUMBERING_ORIGIN_POLYGON_ID) {
                dispatch(selectRestrictionLine(null));
              } else {
                dispatch(selectRestrictionLine(null));
                dispatch(thunkClickOnAPixPolygon(id, coordinates));
              }
            },
            onDrawPath: (start, end) => {
              dispatch(addRestrictionLine({ id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, start, end }));
            },
            onDragAndDrop: (id, points) => {
              if (id === NUMBERING_ORIGIN_POLYGON_ID) {
                const pointCount = points.length;
                if (pointCount === 0) return;
                const [sumX, sumY] = points.reduce<[number, number]>(
                  ([accX, accY], point) => [accX + point[0]!, accY + point[1]!],
                  [0, 0],
                );
                dispatch(setNumberingOrigin([sumX / pointCount, sumY / pointCount]));
                return;
              }

              dispatch(thunkMoveDeviceOnDragEnd({ id, points }));
            },
            onDragSelected: (ids) => dispatch(dragSelectEndThunk(ids)),
          }}
          threeD={{
            fragFiles: fragData?.files ?? [],
            isLoadingFragFiles,
            isFetchingFragFiles,
            fragFilesError,
            visibleMcbimFileNames: uniqueVisibleMcbimFileNames,
            clipZMm,
            onObjectClick: (guid: string) => dispatch(thunkHandle3dObjectClick(guid)),
          }}
        />
      </Box>
    </Box>
  );
};

const StatsBar: React.FC = () => {
  const floorAndJob = useAppSelector((state) => state.design.floorAndJob);
  const nrOfMcBimSpaces = useAppSelector(selectnrOfMcbimSpaces);
  const nrOfMcBimItems = useAppSelector(selectNrOfMcbimItems);

  if (floorAndJob == null) return null;

  return (
    <Flex justify="center" bg="white">
      <h6 style={{ margin: "0 10px" }}>Etasje: {floorAndJob.floorName}</h6>
      <h6 style={{ margin: "0 10px" }}>Rom: {nrOfMcBimSpaces}</h6>
      <h6 style={{ margin: "0 10px" }}>Komponenter: {nrOfMcBimItems}</h6>
    </Flex>
  );
};
